import { startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { DatePickerGridProps } from './DatePickerGrid.types';

/**
 * DatePickerGrid - Renders the selectable grid based on current view (days, months, or years)
 *
 * Handles rendering of:
 * - Day view: Calendar grid with days of the month
 * - Month view: 12 month buttons
 * - Year view: 12-year range buttons
 *
 * @component
 * @param {DatePickerGridProps} props - Component props
 * @returns {JSX.Element} Grid of selectable date/month/year items
 *
 * @example
 * <DatePickerGrid
 *   view="day"
 *   displayDate={new Date()}
 *   value={selectedDate}
 *   onDayClick={handleDay}
 *   onMonthClick={handleMonth}
 *   onYearClick={handleYear}
 * />
 */
export const DatePickerGrid = ({
  view,
  displayDate,
  value,
  onDayClick,
  onMonthClick,
  onYearClick
}: DatePickerGridProps) => {
  /**
   * Renders the day view with calendar grid
   * Displays week days header and all days of the current month
   *
   * @returns {JSX.Element} Calendar grid with days
   */
  const renderDays = () => {
    const start = startOfMonth(displayDate);
    const end = endOfMonth(displayDate);
    const days = eachDayOfInterval({ start, end });
    const firstDayOfWeek = start.getDay();
    const emptyDays = Array(firstDayOfWeek).fill(null);

    return (
      <div className="custom-date-picker-grid">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'].map(day => (
          <div key={day} className="custom-date-picker-weekday">
            {day}
          </div>
        ))}
        {emptyDays.map((_, i) => (
          <div key={`empty-${i}`} className="custom-date-picker-empty"></div>
        ))}
        {days.map(day => (
          <button
            key={day.toISOString()}
            className={`custom-date-picker-day ${value && isSameDay(day, value) ? 'active' : ''}`}
            onClick={() => onDayClick(day)}
          >
            {day.getDate()}
          </button>
        ))}
      </div>
    );
  };

  /**
   * Renders the month view with 12 month buttons
   * Highlights the currently selected month if in the same year
   *
   * @returns {JSX.Element} Grid of month buttons
   */
  const renderMonths = () => {
    const months = [
      'Січень',
      'Лютий',
      'Березень',
      'Квітень',
      'Травень',
      'Червень',
      'Липень',
      'Серпень',
      'Вересень',
      'Жовтень',
      'Листопад',
      'Грудень'
    ];

    return (
      <div className="custom-date-picker-grid">
        {months.map((month, i) => (
          <button
            key={month}
            className={`custom-date-picker-month ${
              value && value.getMonth() === i && value.getFullYear() === displayDate.getFullYear() ? 'active' : ''
            }`}
            onClick={() => onMonthClick(i)}
          >
            {month.slice(0, 3)}
          </button>
        ))}
      </div>
    );
  };

  /**
   * Renders the year view with 12-year range buttons
   * Displays a decade of years starting from the nearest 10-year boundary
   *
   * @returns {JSX.Element} Grid of year buttons
   */
  const renderYears = () => {
    const currentYear = displayDate.getFullYear();
    const startYear = Math.floor(currentYear / 10) * 10;
    const years = Array.from({ length: 12 }, (_, i) => startYear + i);

    return (
      <div className="custom-date-picker-grid">
        {years.map(year => (
          <button
            key={year}
            className={`custom-date-picker-year ${value && value.getFullYear() === year ? 'active' : ''}`}
            onClick={() => onYearClick(year)}
          >
            {year}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="custom-date-picker-content">
      {view === 'day' && renderDays()}
      {view === 'month' && renderMonths()}
      {view === 'year' && renderYears()}
    </div>
  );
};
